/**
 * PriceTravel Booker Widget Entry Point
 * Encapsulated via Shadow DOM for cross-site compatibility.
 */

// --- 1. DEPENDENCIAS ---
import moment from 'moment';
window.moment = moment;
import axios from 'axios';
window.axios = axios;
import $ from 'jquery';
window.$ = window.jQuery = $;

import '../../settings.js';
import Vue from 'vue';
import { PiniaVuePlugin, createPinia } from 'pinia';

// Componentes del Booker
import BookerFlights from './bookerFlights.vue';
import BookerHotels from './bookerHotels.vue';
import BookerPackages from './bookerPackages.vue';

// Configuración de Plugins
if (!Vue.prototype.$pinia) {
    Vue.use(PiniaVuePlugin);
}
const pinia = createPinia();

// --- 2. INTERNACIONALIZACIÓN (I18n) ---
Vue.prototype.__ = function (str, values = []) {
    const _ = window._;
    const i18n = window.i18n || {};
    let formatted = _.get(i18n, str, str);

    if (formatted === str && window.__pt && window.__pt.ln) {
        const key = str.split('.').pop();
        formatted = window.__pt.ln[key] || window.__pt.ln[str] || str;
    }

    if (values && values.length) {
        formatted = formatted.replace(/\{\d\}/g, (match) => {
            const index = match.replace(/\{|\}/g, "");
            return values[index] || match;
        });
    }
    return formatted;
};

// --- 3. GESTIÓN DE ESTILOS Y SHADOW DOM ---
let cachedCSS = null;

/**
 * Prepara los estilos para el Shadow DOM.
 * Descarga el CSS, arregla rutas de assets y extrae fuentes al Head.
 */
const prepareShadowStyles = async (cssUrl) => {
    if (cachedCSS) return cachedCSS;

    try {
        const response = await fetch(cssUrl);
        let cssText = await response.text();
        const baseUrl = cssUrl.substring(0, cssUrl.lastIndexOf('/') + 1);

        // A. Arreglar rutas de assets (imágenes, fuentes) a URLs absolutas
        cssText = cssText.replace(/url\(["']?(.+?)["']?\)/g, (match, path) => {
            if (path.startsWith('http') || path.startsWith('/') || path.startsWith('data:')) return match;
            return `url("${new URL(path, baseUrl).href}")`;
        });

        // B. Mirroring de Fuentes: Extraer @font-face al Head (evita fallos de carga en Shadow DOM)
        const fontRules = cssText.match(/@font-face\s*{[\s\S]*?}/g);
        if (fontRules && !document.getElementById('pt-booker-fonts')) {
            const styleElement = document.createElement('style');
            styleElement.id = 'pt-booker-fonts';
            styleElement.textContent = fontRules.join('\n');
            document.head.appendChild(styleElement);
        }

        // C. Parche de Selectores: Reemplazar :root y body por :host para aislamiento total
        cachedCSS = cssText.replace(/:root/g, ':host').replace(/\bbody\b/g, ':host');

        return cachedCSS;
    } catch (error) {
        console.error("[Booker] Error al procesar estilos:", error);
        return "";
    }
};

// --- 4. INICIALIZACIÓN Y MONTAJE ---

/**
 * Asegura que los scripts de traducción estén cargados antes del montaje.
 */
const ensureLanguage = () => {
    return new Promise((resolve) => {
        const culture = window.__pt.culture.cultureCode;
        const baseUrl = window.__pt.settings.site.siteUrl;
        const script = document.createElement('script');
        script.src = `${baseUrl}/assets/js/vue/lang/${culture}.js`;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
    });
};

/**
 * Orquestador principal del montaje del widget.
 */
const mountBooker = async () => {
    // A. Configuración Base
    await ensureLanguage();
    const { siteUrl, siteName } = window.__pt.settings.site;
    const culture = window.__pt.culture.cultureCode;
    const cssUrl = `${siteUrl}/assets/css/dist/${siteName}/booker-share.css`;

    // B. Preparar Estilos
    const widgetStyles = await prepareShadowStyles(cssUrl);

    // C. Registro de Componentes
    const widgetMap = [
        { selector: 'booker-hotel', component: BookerHotels },
        { selector: 'booker-flight', component: BookerFlights },
        { selector: 'booker-package', component: BookerPackages }
    ];

    // D. Proceso de Montaje
    widgetMap.forEach(({ selector, component }) => {
        const containers = document.querySelectorAll(selector);

        containers.forEach(el => {
            if (el.shadowRoot) return; // Evitar doble montaje

            // 1. Inicializar Shadow Root
            const shadow = el.attachShadow({ mode: 'open' });

            // 2. Inyectar Estilos Aislados
            const styleTag = document.createElement('style');
            styleTag.textContent = widgetStyles;
            shadow.appendChild(styleTag);

            // 3. Clonar Estilos de Vue (Scoped Styles)
            document.querySelectorAll('style').forEach(s => {
                if (s.id !== 'pt-booker-fonts' && s.textContent !== widgetStyles) {
                    shadow.appendChild(s.cloneNode(true));
                }
            });

            // 4. Crear Contenedor Raíz Interno
            const appContainer = document.createElement('div');
            appContainer.className = `${siteName} home ${culture} booker`;
            appContainer.style.cssText = 'display:block; position:relative; min-height:100%';
            shadow.appendChild(appContainer);

            // 5. Montar Aplicación Vue
            new Vue({
                pinia,
                render: h => h(component, { props: { ...el.dataset } })
            }).$mount(appContainer);
        });
    });
};

// --- 5. EJECUCIÓN ---
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountBooker);
} else {
    mountBooker();
}

// Exportación para uso modular
export { BookerHotels, BookerFlights, BookerPackages };
